import { toast } from "react-hot-toast";

// Toast utility functions that can be called from anywhere
export const showToast = {
  // Success toast
  success: (message, options = {}) => {
    toast.success(message, {
      duration: 3000,
      position: "top-right",
      style: {
        background: "#F0FDF4",
        color: "#166534",
        border: "1px solid #BBF7D0",
      },
      ...options,
    });
  },

  // Error toast
  error: (message, options = {}) => {
    toast.error(message, {
      duration: 5000,
      position: "top-right",
      style: {
        background: "#FEF2F2",
        color: "#DC2626",
        border: "1px solid #FECACA",
      },
      ...options,
    });
  },

  // Info toast
  info: (message, options = {}) => {
    toast(message, {
      duration: 4000,
      position: "top-right",
      style: {
        background: "#EFF6FF",
        color: "#1E40AF",
        border: "1px solid #BFDBFE",
      },
      ...options,
    });
  },

  // Warning toast
  warning: (message, options = {}) => {
    toast(message, {
      duration: 4000,
      position: "top-right",
      style: {
        background: "#FFFBEB",
        color: "#92400E",
        border: "1px solid #FED7AA",
      },
      icon: "⚠️",
      ...options,
    });
  },

  // Loading toast (returns toast ID for dismissal)
  loading: (message, options = {}) => {
    return toast.loading(message, {
      position: "top-right",
      style: {
        background: "#F0F9FF",
        color: "#0369A1",
        border: "1px solid #BAE6FD",
      },
      ...options,
    });
  },

  // Dismiss specific toast
  dismiss: (toastId) => {
    toast.dismiss(toastId);
  },

  // Dismiss all toasts
  dismissAll: () => {
    toast.dismiss();
  },
};

// Convenience functions for common use cases
export const showApiError = (
  error,
  fallbackMessage = "Something went wrong"
) => {
  try {
    console.log('showApiError - error:', error);
    console.log('showApiError - error type:', typeof error);
    
    // If error is null or undefined, show fallback
    if (!error) {
      showToast.error(String(fallbackMessage));
      return;
    }

    // If error is a string, show it directly
    if (typeof error === 'string') {
      showToast.error(String(error));
      return;
    }

    // Extract the response data
    const responseData = error?.response?.data;
    
    // Check if there's a message in the response
    if (responseData?.message) {
      showToast.error(String(responseData.message));
      return;
    }

    // Handle validation errors in data field
    const validationErrors = responseData?.data;
    
    if (validationErrors && typeof validationErrors === 'object' && !Array.isArray(validationErrors) && Object.keys(validationErrors).length > 0) {
      // Extract all error messages from the validation errors object
      const errorMessages = [];
      
      Object.keys(validationErrors).forEach((field) => {
        const fieldErrors = validationErrors[field];
        if (Array.isArray(fieldErrors)) {
          // If it's an array of errors, add all of them
          errorMessages.push(...fieldErrors);
        } else if (typeof fieldErrors === 'string') {
          // If it's a single string error
          errorMessages.push(fieldErrors);
        }
      });
      
      // Show each error message as a separate toast
      if (errorMessages.length > 0) {
        errorMessages.forEach((msg) => {
          showToast.error(String(msg));
        });
        return;
      }
    }
    
    // Fallback to the main error message or fallback
    const message =
      error?.message || 
      error?.response?.statusText || 
      fallbackMessage;
    showToast.error(String(message));
  } catch (toastError) {
    // If anything goes wrong in error handling, show a safe fallback
    console.error('Error in showApiError:', toastError);
    showToast.error("An error occurred");
  }
};

export const showApiSuccess = (
  message = "Operation completed successfully"
) => {
  showToast.success(message);
};

// For handling errors from unwrap() which returns the rejected value directly
export const showUnwrapError = (error, fallbackMessage = "Something went wrong") => {
  const message = typeof error === 'string' ? error : (error?.message || fallbackMessage);
  showToast.error(message);
};

export const showValidationError = (message) => {
  showToast.error(message);
};

export const showInfo = (message) => {
  showToast.info(message);
};

export const showWarning = (message) => {
  showToast.warning(message);
};
